import { Markdown } from '@/src/components/common/Markdown';
import userGuideMd from './UserGuide.md';
import GithubSlugger from 'github-slugger';
import styles from './UserGuide.module.scss';
import { useEffect, useState } from 'react';
import { TextContent } from '@amzn/awsui-components-react';
import { useSideNav } from '@/src/hooks/store/useSideNav';
import { isProd } from '@/src/utils/env.utils';
import { config } from '@/src/config/app-config';
import { preProdLogger } from '@/src/utils/log.utils';

interface TocLink {
  text: string;
  href: string;
  type: 'link';
}

interface TocExpandableLinkGroup {
  text: string;
  href: string;
  type: 'expandable-link-group';
  items: TocItem[];
}

type TocItem = TocLink | TocExpandableLinkGroup;

/**
 * Strips inline markdown syntax so the plain text matches what remark-slug
 * sees after rendering (e.g. `**bold**` → `bold`, `[text](url)` → `text`).
 */
function stripInlineMarkdown(text: string): string {
  return (
    text
      // Images: ![alt](url) → alt
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
      // Links: [text](url) → text
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      // Inline code: `code` → code
      .replace(/`([^`]*)`/g, '$1')
      // Bold/italic: ***text***, **text**, *text*, ___text___, __text__, _text_
      .replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1')
      .replace(/_{1,3}([^_]+)_{1,3}/g, '$1')
      // Strikethrough: ~~text~~ → text
      .replace(/~~([^~]+)~~/g, '$1')
  );
}

function extractHeadings(markdown: string): TocItem[] {
  const slugger = new GithubSlugger();
  const headingRegex = /^(#{1,6})\s+(.+)$/gm;
  const flat: { level: number; text: string; href: string }[] = [];
  let match: RegExpExecArray | null;
  const withoutCodeBlocks = markdown.replace(/```[\s\S]*?```/g, '');

  while ((match = headingRegex.exec(withoutCodeBlocks)) !== null) {
    const level = match[1].length;
    const rawText = match[2].trim();
    const text = stripInlineMarkdown(rawText);
    const href = `#${slugger.slug(text)}`;
    flat.push({ level, text, href });
  }

  function buildTree(entries: { level: number; text: string; href: string }[], parentLevel: number): TocItem[] {
    const result: TocItem[] = [];

    let i = 0;
    while (i < entries.length) {
      const entry = entries[i];

      // Collect all subsequent entries that are deeper than this one (children)
      const children: { level: number; text: string; href: string }[] = [];
      let j = i + 1;
      while (j < entries.length && entries[j].level > entry.level) {
        children.push(entries[j]);
        j++;
      }

      if (children.length > 0) {
        result.push({
          text: entry.text,
          href: entry.href,
          type: 'expandable-link-group',
          items: buildTree(children, entry.level),
        });
      } else {
        result.push({
          text: entry.text,
          href: entry.href,
          type: 'link',
        });
      }

      i = j;
    }

    return result;
  }

  return buildTree(flat, 0);
}

const SOURCE_BUCKET = isProd() ? config?.s3?.publicDocsBucketName : config?.s3?.challengeResourcesBucketName;

const BUCKET_LOCATION = SOURCE_BUCKET
  ? `https://${SOURCE_BUCKET}.s3.us-west-2.amazonaws.com/user-guide/user-guide`
  : '';

export const UserGuide: React.FC = () => {
  const [source, setSource] = useState('');
  const [usedFallback, setUsedFallback] = useState(false);
  const { setUserGuideToc } = useSideNav();
  useEffect(() => {
    if (config.ff.enableS3HostedUserGuide) {
      fetch(BUCKET_LOCATION + '/GUIDE_2026.md')
        .then((res) => {
          if (!res.ok) {
            throw new Error(`Failed to load user guide: ${res?.status}`);
          }
          return res.text();
        })
        .then((md) => {
          setUserGuideToc(extractHeadings(md));
          setSource(md);
        })
        .catch((err) => {
          preProdLogger('Error fetching/parsing user guide', err);
          setUsedFallback(true);
          setUserGuideToc(extractHeadings(userGuideMd));
          setSource(userGuideMd);
        });
    } else {
      setSource(userGuideMd);
    }
  }, []);

  const useS3Features = config.ff.enableS3HostedUserGuide && !usedFallback;

  return (
    <div className={styles.markdown}>
      <TextContent>
        <Markdown content={source} generateToc imageSrcBase={useS3Features ? BUCKET_LOCATION : undefined} />
      </TextContent>
    </div>
  );
};
